import React, { ChangeEvent, KeyboardEvent, useEffect, useState } from 'react';

export interface NumberInputProps {
	value?: number;
	onChange?: (value?: number) => void;
	step?: number;
	className?: string;
	places?: number;
	placeholder?: string;
	tabIndex?: number;
	disabled?: boolean;
}

export function NumberInput({
	value,
	onChange,
	step = 1,
	places,
	placeholder,
	className,
	tabIndex,
	disabled
}: NumberInputProps) {
	const [state, setState] = useState(value?.toString() ?? '');

	const handleChange = (evt: ChangeEvent<HTMLInputElement>) => {
		if (evt.target.value === '' || evt.target.value === '-') {
			setState(evt.target.value);
			if (onChange !== undefined) {
				onChange(undefined);
			}
		} else {
			const newValue = parseFloat(evt.target.value);
			if (!Number.isNaN(newValue) && newValue < Infinity) {
				setState(evt.target.value);
				if (onChange !== undefined) {
					onChange(newValue);
				}
			}
		}
	};

	useEffect(() => {
		setState(value?.toString() ?? '');
	}, [value]);

	const handleKeyDown = (evt: KeyboardEvent<HTMLInputElement>) => {
		switch (evt.key) {
			case 'Backspace':
			case 'Delete':
			case 'ArrowLeft':
			case 'ArrowRight':
				return;
			case 'ArrowUp':
				if (onChange !== undefined) {
					onChange((value ?? 0) + step);
				}
				evt.preventDefault();
				evt.stopPropagation();
				break;
			case 'ArrowDown':
				if (onChange !== undefined) {
					onChange((value ?? 0) - step);
				}
				evt.preventDefault();
				evt.stopPropagation();
				break;
			case '-':
				if (evt.currentTarget.selectionStart !== 0) {
					evt.preventDefault();
					evt.stopPropagation();
				}
				break;
			case '.':
				// only allow ONE '.' in the value
				if (places === 0 || state.indexOf('.') !== -1) {
					evt.preventDefault();
					evt.stopPropagation();
				}
				break;
			default:
				// Only allow numeric keypresses if not handled specifically above
				if (evt.key < '0' || evt.key > '9') {
					evt.preventDefault();
					evt.stopPropagation();
				} else {
					if (
						places !== undefined &&
						state.indexOf('.') !== -1 &&
						state.length - state.indexOf('.') - 1 === places &&
						(evt.currentTarget?.selectionStart ?? 0) > state.indexOf('.')
					) {
						//
						evt.preventDefault();
						evt.stopPropagation();
						return;
					}
					const newValue = parseFloat(state + evt.key);
					if (isNaN(newValue) || newValue === Infinity) {
						evt.preventDefault();
						evt.stopPropagation();
					}
				}
				break;
		}
	};
	return (
		<input
			className={className}
			type="text"
			value={state}
			onChange={handleChange}
			onKeyDown={handleKeyDown}
			placeholder={placeholder}
			onBlur={handleChange}
			tabIndex={tabIndex}
			disabled={disabled}
		/>
	);
}
